import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography } from "@material-ui/core";
import { Page, PageContent, ScrollToTopOnMount } from "frame/components";
import { useRouter } from "utils";
import SiteInspectionChecklistCard from "./site-inspection-checklist-card";
import EditSiteInspectionChecklistForm from "../edit-site-inspection-checklist-form";
import { getMockSiteInspectionChecklists } from "../models/mock-data";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
	},
	heading: {
		marginBottom: theme.spacing(3),
	},
	body: {
		minHeight: 300,
	},
	empty: {
		marginTop: theme.spacing(2),
		textAlign: "center",
	},
	embeddedRoot: {
		maxHeight: "min(70vh, 720px)",
		overflowY: "auto",
	},
	embeddedContent: {
		paddingTop: theme.spacing(1),
	},
}));

const SiteInspectionChecklistSiteList = ({
	role,
	onDelete,
	onEmail,
	variant = "page",
	siteId: siteIdProp,
}) => {
	const classes = useStyles();
	const { match } = useRouter();
	const siteId = siteIdProp || match?.params?.siteId;
	const isEmbedded = variant === "embedded";

	const [editId, setEditId] = useState();
	const [viewId, setViewId] = useState(null);
	const [siteSIC, setSiteSIC] = useState(null);
	const [isClone, setIsClone] = useState(false);

	useEffect(() => {
		const mockData = getMockSiteInspectionChecklists();
		setSiteSIC(
			mockData && siteId
				? mockData.filter((node) => node.siteId === siteId)
				: []
		);
	}, [siteId]);

	const empty = siteSIC && siteSIC.length <= 0;

	const onView = (id) => () => setViewId(id);

	const onEdit = (id) => () => setEditId(id);

	const onClone = (id) => () => {
		setIsClone(true);
		setEditId(id);
	};

	const onCancelEdit = () => {
		setEditId(null);
		setViewId(null);
		setIsClone(false);
	};

	const forms = (
		<>
			{viewId && (
				<EditSiteInspectionChecklistForm
					id={viewId}
					readOnly
					onClose={onCancelEdit}
				/>
			)}
			{editId && (
				<EditSiteInspectionChecklistForm
					id={editId}
					isClone={isClone}
					onClose={onCancelEdit}
				/>
			)}
		</>
	);

	const body = (
		<PageContent pad className={isEmbedded ? classes.embeddedContent : undefined}>
			{!isEmbedded && (
				<Typography variant="h2" className={classes.heading}>
					Site Inspection Checklist
				</Typography>
			)}

			{!empty && (
				<div className={classes.body}>
					{siteSIC &&
						siteSIC.map((item, index) => (
							<SiteInspectionChecklistCard
								key={index}
								siteInspectionChecklist={item}
								onView={onView(item.id)}
								onEdit={onEdit(item.id)}
								onClone={onClone(item.id)}
								onDelete={onDelete}
								role={role}
								onEmail={onEmail}
							/>
						))}
				</div>
			)}

			{empty && (
				<Typography className={classes.empty} variant="body1">
					There are currently no Site Inspection Checklists listed.
				</Typography>
			)}
		</PageContent>
	);

	if (isEmbedded) {
		return (
			<div className={classes.embeddedRoot}>
				{body}
				{forms}
			</div>
		);
	}

	return (
		<Page className={classes.root} title="Site Inspection Checklist">
			<ScrollToTopOnMount />
			{body}
			{forms}
		</Page>
	);
};

SiteInspectionChecklistSiteList.propTypes = {
	role: PropTypes.string.isRequired,
	onDelete: PropTypes.func.isRequired,
	onEmail: PropTypes.func,
	variant: PropTypes.oneOf(["page", "embedded"]),
	siteId: PropTypes.string,
};

SiteInspectionChecklistSiteList.defaultProps = {
	onEmail: () => {},
};

export default SiteInspectionChecklistSiteList;
